import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BellIcon, MenuIcon, ChevronDownIcon } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import NotificationsDropdown from './NotificationsDropdown';
import CompanySelector from './CompanySelector';
import PeriodSelector from './PeriodSelector';
import UserMenu from './UserMenu';
import { notificationsData } from '../../utils/mockData';
interface HeaderProps {
  onToggleMobileMenu: () => void;
}
const Header: React.FC<HeaderProps> = ({
  onToggleMobileMenu
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState(notificationsData);
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Simular que el usuario es Super Admin - en producción esto vendría del contexto de autenticación
  const isSuperAdmin = true;
  // Rutas donde se deben mostrar los selectores de empresa y período
  const financialViewRoutes = ['/', '/income-statement', '/cash-flow', '/balance-sheet', '/trends', '/settings'];
  // Rutas donde se debe mostrar SOLO el selector de período
  const periodSelectorRoutes = ['/income-statement', '/cash-flow', '/balance-sheet'];
  // Verificar si la ruta actual es una vista financiera
  const isFinancialView = financialViewRoutes.includes(location.pathname);
  // Verificar si la ruta actual debe mostrar el selector de período
  const showPeriodSelector = periodSelectorRoutes.includes(location.pathname);
  // Map routes to page titles
  const getPageTitle = () => {
    const titleMap: {
      [key: string]: string;
    } = {
      '/': 'Dashboard',
      '/income-statement': 'Estado de Resultados',
      '/cash-flow': 'Flujo de Efectivo',
      '/balance-sheet': 'Balance General',
      '/trends': 'Tendencias',
      '/settings': 'Configuración',
      '/account': 'Configuración de Cuenta',
      '/admin': 'Panel de Administración',
      '/admin/companies': 'Gestión de Empresas',
      '/admin/users': 'Gestión de Usuarios',
      '/admin/data-import': 'Importación de Datos'
    };
    return titleMap[location.pathname] || 'Dashboard';
  };
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const unreadCount = notifications.filter(n => !n.read).length;
  const handleMarkAsRead = (id: number) => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      read: true
    })));
  };
  const handleDelete = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  return <header className="bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center" data-id="element-93">
      <div className="flex items-center space-x-4" data-id="element-94">
        <button onClick={onToggleMobileMenu} className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Toggle menu" data-id="element-95">
          <MenuIcon size={24} className="text-gray-600" data-id="element-96" />
        </button>
        <div data-id="element-97">
          <h2 className="hidden lg:block text-xl font-semibold text-gray-800" data-id="element-98">
            {getPageTitle()}
          </h2>
        </div>
      </div>
      <div className="flex items-center space-x-3" data-id="element-99">
        {isSuperAdmin && isFinancialView && <>
            <CompanySelector data-id="element-100" />
            {showPeriodSelector && <PeriodSelector data-id="element-101" />}
          </>}
        <div className="relative" ref={notificationRef} data-id="element-102">
          <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-103">
            <BellIcon size={20} className="text-gray-600" data-id="element-104" />
            {unreadCount > 0 && <span className="absolute top-0 right-0 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-medium" data-id="element-105">
                {unreadCount}
              </span>}
          </button>
          <AnimatePresence data-id="element-106">
            {showNotifications && <NotificationsDropdown notifications={notifications} onMarkAsRead={handleMarkAsRead} onMarkAllAsRead={handleMarkAllAsRead} onDelete={handleDelete} onClose={() => setShowNotifications(false)} data-id="element-107" />}
          </AnimatePresence>
        </div>
        <div className="relative" ref={userMenuRef} data-id="element-108">
          <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg p-2 transition-colors" data-id="element-109">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center" data-id="element-110">
              <span className="text-white font-medium text-sm" data-id="element-111">RS</span>
            </div>
            <div className="hidden lg:flex flex-1 text-left" data-id="element-112">
              <div data-id="element-113">
                <p className="text-sm font-medium" data-id="element-114">Roberto Sánchez</p>
                <p className="text-xs text-gray-500" data-id="element-115">Super Admin</p>
              </div>
            </div>
            <ChevronDownIcon size={16} className={`hidden md:block text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} data-id="element-116" />
          </button>
          <UserMenu isOpen={showUserMenu} onClose={() => setShowUserMenu(false)} data-id="element-117" />
        </div>
      </div>
    </header>;
};
export default Header;